import { useForm } from 'react-hook-form';
import { Modal, Input, Select, Button } from '@/components/ui';
import { useClients } from '@/hooks/useClients';
import { useCreateProject, useUpdateProject } from '@/hooks/useProjects';
import type { CreateProjectInput, Project } from '@aelita/shared';

export interface ProjectFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project | null;
  /** Pre-select a client when creating a new project */
  defaultClientId?: string;
}

export function ProjectFormModal({
  isOpen,
  onClose,
  project,
  defaultClientId,
}: ProjectFormModalProps) {
  const { data: clientsData } = useClients();
  const clients = clientsData?.data ?? [];
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const isEditing = !!project;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateProjectInput>({
    defaultValues: project
      ? {
          name: project.name,
          description: project.description ?? '',
          clientId: project.clientId ?? '',
          status: project.status,
          type: project.type,
          budgetHours: project.budgetHours ?? undefined,
          budgetAmount: project.budgetAmount ?? undefined,
        }
      : {
          clientId: defaultClientId ?? '',
        },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateProjectInput) => {
    try {
      if (isEditing) {
        await updateProject.mutateAsync({
          id: project.id,
          data: {
            ...data,
            clientId: data.clientId || null,
          },
        });
      } else {
        await createProject.mutateAsync({
          ...data,
          clientId: data.clientId || null,
        });
      }
      reset();
      onClose();
    } catch (error) {
      console.error('Failed to save project:', error);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? 'Edit Project' : 'Create New Project'}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Project Name"
          {...register('name', { required: 'Name is required' })}
          error={errors.name?.message}
          defaultValue={project?.name}
        />

        <div>
          <label className="label">Description</label>
          <textarea
            {...register('description')}
            className="input min-h-[80px]"
            placeholder="Project description..."
            defaultValue={project?.description ?? ''}
          />
        </div>

        <Select
          label="Client"
          {...register('clientId')}
          options={[
            { value: '', label: 'Internal Project' },
            ...clients.map((c: any) => ({ value: c.id, label: c.name })),
          ]}
          defaultValue={project?.clientId ?? ''}
        />

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Status"
            {...register('status')}
            options={[
              { value: 'pipeline', label: 'Pipeline' },
              { value: 'active', label: 'Active' },
              { value: 'on_hold', label: 'On Hold' },
              { value: 'completed', label: 'Completed' },
              { value: 'cancelled', label: 'Cancelled' },
            ]}
            defaultValue={project?.status ?? 'pipeline'}
          />

          <Select
            label="Type"
            {...register('type')}
            options={[
              { value: 'client', label: 'Client' },
              { value: 'internal', label: 'Internal' },
              { value: 'rnd', label: 'R&D' },
              { value: 'maintenance', label: 'Maintenance' },
            ]}
            defaultValue={project?.type ?? 'client'}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Budget Hours"
            type="number"
            {...register('budgetHours', { valueAsNumber: true })}
            defaultValue={project?.budgetHours ?? ''}
          />
          <Input
            label="Budget Amount"
            type="number"
            {...register('budgetAmount', { valueAsNumber: true })}
            defaultValue={project?.budgetAmount ?? ''}
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" isLoading={createProject.isPending || updateProject.isPending}>
            {isEditing ? 'Save Changes' : 'Create Project'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
